import React, { useState } from 'react';
import {
  Button,
  ButtonGroup,
  Drawer,
  Modal,
  Text,
  Form,
} from '@common/components';
import { useMutation, ApolloError, useNavigate } from '@common/hooks';
import { PerformAssignmentAction } from 'works/legacy/graphql/staffing-request-assignment';
import { ASSIGNMENT_STATE_INFO } from 'works/legacy/components/assignment-state-pill';
import { mutationResponseMessage } from 'works/legacy/lib/message-helper';
import { Store } from 'antd/lib/form/interface';
import { CancelAssignmentForm } from './cancel-assignment-form';
import { buildDialogUrl } from '@common/services';

export function CancelAssignmentDrawer({
  open,
  onClose,
  assignmentId,
}: {
  open: boolean;
  onClose: () => void;
  assignmentId?: string;
}) {
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const [redirectToWorkerStatusSetup, setRedirectToWorkerStatusSetup] =
    useState(false);

  const [performAction] = useMutation(PerformAssignmentAction, {
    refetchQueries: ['assignmentTerm'],
  });

  const handleCancelledDrawerConfirm = (reason: string, details: string) => {
    performAction({
      variables: {
        staffingRequestAssignmentId: assignmentId,
        input: {
          action: ASSIGNMENT_STATE_INFO.cancelled.action,
          cancelledReason: reason,
          cancelledDetail: details,
        },
      },
    }).then((response) =>
      mutationResponseMessage({
        gqlResponse: response as {
          data?: unknown;
          errors?: ApolloError[];
        },
        onSuccess: 'Assignment status successfully changed',
        errorMessage: 'Failed to update assignment status',
      })
    );
  };

  const profileStatusSetupAssignmentDrawerUrl = buildDialogUrl(
    'profile-status-setup-assignment-drawer',
    {
      pathName: `/works/team/long_term/${assignmentId}/assignment`,
      overlap: false,
      props: {
        assignmentId: assignmentId || '',
        dnr: true,
      },
    }
  );

  const handleSubmit = (values: Store) => {
    handleCancelledDrawerConfirm(values.reason, values.details);

    if (redirectToWorkerStatusSetup) {
      navigate(profileStatusSetupAssignmentDrawerUrl);
    } else {
      onClose();
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Cancel Assignment"
      className="dispute-create-drawer"
      footer={
        <ButtonGroup>
          <Button key="back" onClick={onClose}>
            Back
          </Button>
          <Button
            primary
            danger
            onClick={() =>
              confirmCancelAssignment({
                onConfirm: form.submit,
                redirectToWorkerStatusSetup,
              })
            }
          >
            Cancel Assignment
          </Button>
        </ButtonGroup>
      }
    >
      <CancelAssignmentForm
        form={form}
        onSubmit={handleSubmit}
        assignmentId={assignmentId}
        redirectToWorkerStatusSetup={redirectToWorkerStatusSetup}
        setRedirectToWorkerStatusSetup={setRedirectToWorkerStatusSetup}
      />
    </Drawer>
  );
}

function confirmCancelAssignment({
  onConfirm,
  redirectToWorkerStatusSetup,
}: {
  onConfirm: () => void;
  redirectToWorkerStatusSetup: boolean;
}) {
  Modal.confirm({
    title: 'Are you sure you want to cancel this assignment?',
    onOk: onConfirm,
    content: (
      <>
        <Text block>
          This action can&apos;t be undone. A notification will be sent to the
          supplier.
        </Text>
        {redirectToWorkerStatusSetup && (
          <Text>
            You will be redirected to the Do Not Rehire setup after confirming.
          </Text>
        )}
      </>
    ),
  });
}
